import { useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { TOUR } from "../constants/testIds";
import { Loader2, CheckCircle2, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeadRequestModal = ({ open, onOpenChange, tour = null, source = "lead", filters = null }) => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    telegram: "",
    destination: "",
    budget: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () => {
    setForm({ name: "", phone: "", telegram: "", destination: "", budget: "", notes: "" });
    setSuccess(false);
  };

  const handleClose = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(reset, 200);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Пожалуйста, укажите имя и телефон");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, source };
      if (filters) {
        payload.filters = {
          city_from: filters.from || "",
          destination: filters.destination || "",
          date: filters.date || "",
          nights: filters.nights || "",
          adults: filters.adults || "",
          children: filters.children || "",
        };
        if (!payload.destination) payload.destination = filters.destination || "";
      }
      if (tour) {
        payload.tour = {
          hotel: tour.hotel,
          country: tour.country,
          city: tour.city,
          stars: tour.stars,
          rating: tour.rating,
          food: tour.food,
          airline: tour.airline,
          room: tour.room,
          nights: tour.nights,
          date: tour.date,
          price: tour.price,
          old_price: tour.old_price,
          discount: tour.discount,
        };
        if (!payload.destination) payload.destination = tour.country;
      }
      await axios.post(`${API}/tour-requests`, payload);
      setSuccess(true);
    } catch (err) {
      toast.error("Не удалось отправить заявку. Попробуйте позвонить нам напрямую.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid={TOUR.leadDialog}
        className="max-w-lg bg-white border-0 shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        {success ? (
          <div
            data-testid={TOUR.leadSuccess}
            className="text-center py-6 animate-fade-up"
          >
            <div className="mx-auto w-16 h-16 rounded-full bg-teal/10 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-9 h-9 text-teal" />
            </div>
            <h3 className="font-display text-2xl mb-2 text-[#063B4E]">
              ✅ Спасибо!
            </h3>
            <p className="text-[#4A5A6B] mb-1">
              Мы получили вашу заявку.
            </p>
            <p className="text-[#4A5A6B] mb-6">
              Менеджер свяжется с вами в течение 15 минут.
            </p>
            <Button
              onClick={() => handleClose(false)}
              className="bg-teal hover:bg-[#0a616a] text-white rounded-full px-8 py-6 h-auto"
            >
              Хорошо
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-[#063B4E]">
                {tour ? "Заявка на тур" : "Индивидуальный подбор тура"}
              </DialogTitle>
              <DialogDescription className="text-[#4A5A6B]">
                Оставьте контакты — менеджер уточнит стоимость и наличие,
                после чего свяжется с вами.
              </DialogDescription>
            </DialogHeader>

            {tour && (
              <div
                data-testid={TOUR.leadTourInfo}
                className="rounded-xl bg-[#FFF7EC] border border-[#F0DFC1] p-4 flex gap-3"
              >
                <img
                  src={tour.image}
                  alt={tour.hotel}
                  className="w-20 h-20 object-cover rounded-lg shrink-0"
                />
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-widest text-coral">
                    Предварительное предложение
                  </div>
                  <div className="font-display text-lg text-[#063B4E] leading-tight truncate">
                    {tour.hotel}
                  </div>
                  <div className="text-sm text-[#4A5A6B] flex items-center gap-2 mt-0.5">
                    <span>{tour.country}, {tour.city}</span>
                    <span className="inline-flex text-yellow-500">
                      {Array.from({ length: tour.stars }).map((_, i) => (
                        <Star key={i} className="w-3 h-3 fill-current" />
                      ))}
                    </span>
                  </div>
                  <div className="text-sm mt-1 text-[#063B4E]">
                    <span className="font-semibold">{tour.price}</span>
                    <span className="text-[#8A99A8] mx-1">·</span>
                    {tour.nights} ночей · {tour.date}
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={submit} className="space-y-4 pt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="lead-name" className="text-[#1B2A3B]">Ваше имя *</Label>
                  <Input
                    id="lead-name"
                    data-testid={TOUR.leadName}
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Как к вам обращаться"
                    className="mt-1.5 h-11 bg-[#FFF7EC] border-[#E9D9BE] focus-visible:ring-teal"
                  />
                </div>
                <div>
                  <Label htmlFor="lead-phone" className="text-[#1B2A3B]">Телефон *</Label>
                  <Input
                    id="lead-phone"
                    data-testid={TOUR.leadPhone}
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+7 ___ ___ __ __"
                    className="mt-1.5 h-11 bg-[#FFF7EC] border-[#E9D9BE] focus-visible:ring-teal"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="lead-tg" className="text-[#1B2A3B]">Telegram</Label>
                  <Input
                    id="lead-tg"
                    data-testid={TOUR.leadTelegram}
                    value={form.telegram}
                    onChange={update("telegram")}
                    placeholder="@username"
                    className="mt-1.5 h-11 bg-[#FFF7EC] border-[#E9D9BE] focus-visible:ring-teal"
                  />
                </div>
                {!tour && (
                  <div>
                    <Label htmlFor="lead-destination" className="text-[#1B2A3B]">Направление</Label>
                    <Input
                      id="lead-destination"
                      data-testid={TOUR.leadDestination}
                      value={form.destination}
                      onChange={update("destination")}
                      placeholder="Турция, ОАЭ, Мальдивы..."
                      className="mt-1.5 h-11 bg-[#FFF7EC] border-[#E9D9BE] focus-visible:ring-teal"
                    />
                  </div>
                )}
                {tour && (
                  <div>
                    <Label htmlFor="lead-budget" className="text-[#1B2A3B]">Бюджет</Label>
                    <Input
                      id="lead-budget"
                      data-testid={TOUR.leadBudget}
                      value={form.budget}
                      onChange={update("budget")}
                      placeholder="Например, 150 000 ₽"
                      className="mt-1.5 h-11 bg-[#FFF7EC] border-[#E9D9BE] focus-visible:ring-teal"
                    />
                  </div>
                )}
              </div>

              {!tour && (
                <div>
                  <Label htmlFor="lead-budget2" className="text-[#1B2A3B]">Бюджет</Label>
                  <Input
                    id="lead-budget2"
                    data-testid={TOUR.leadBudget}
                    value={form.budget}
                    onChange={update("budget")}
                    placeholder="Например, 150 000 ₽ на двоих"
                    className="mt-1.5 h-11 bg-[#FFF7EC] border-[#E9D9BE] focus-visible:ring-teal"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="lead-notes" className="text-[#1B2A3B]">Комментарий</Label>
                <Textarea
                  id="lead-notes"
                  data-testid={TOUR.leadNotes}
                  value={form.notes}
                  onChange={update("notes")}
                  placeholder="Даты, город вылета, количество человек, категория отеля"
                  rows={3}
                  className="mt-1.5 bg-[#FFF7EC] border-[#E9D9BE] focus-visible:ring-teal resize-none"
                />
              </div>

              <DialogFooter className="gap-2 sm:gap-2 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  data-testid={TOUR.leadCancel}
                  onClick={() => handleClose(false)}
                  className="text-[#4A5A6B] hover:text-[#063B4E]"
                >
                  Отмена
                </Button>
                <Button
                  type="submit"
                  data-testid={TOUR.leadSubmit}
                  disabled={submitting}
                  className="bg-coral hover:bg-[#d95a3f] text-white rounded-full px-8 py-6 h-auto font-semibold shadow-lg shadow-coral/20"
                >
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Отправить заявку
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
